import { useRef, useEffect, useCallback } from 'react'
import { useNodes } from '@xyflow/react'
import type { Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { aiServiceConfig } from '@bandos/shared'

// Default configuration - uses shared config as defaults
// These can be overridden per-hook via config options
const DEFAULT_DEBOUNCE_MS = aiServiceConfig.REGENERATION_DEBOUNCE_MS
const DEFAULT_MIN_CONTENT_LENGTH = aiServiceConfig.MIN_CONTENT_LENGTH
const DEFAULT_MIN_WORD_CHANGE = 0 // 0 means any change triggers (override with aiServiceConfig.MIN_WORD_CHANGE_THRESHOLD for consistency)

/**
 * Create a word frequency map (bag of words) from content.
 * Counts occurrences of each word for accurate change detection.
 */
function getWordBag(content: string): Map<string, number> {
  const words = content
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0)
  const bag = new Map<string, number>()
  for (const word of words) {
    bag.set(word, (bag.get(word) ?? 0) + 1)
  }
  return bag
}

/**
 * Check if content change is significant based on bag-of-words comparison.
 * Returns true if at least minWordChange words were added/removed/changed.
 * If minWordChange is 0, any change is considered significant.
 *
 * This approach detects actual word changes, not just count changes:
 * - Changing "hello" to "goodbye" counts as 1 word change
 * - Adding "new word" counts as 2 word changes
 * - Changing 1 word in each of 3 fields counts as 3 word changes
 */
function hasSignificantContentChange(
  oldContent: string,
  newContent: string,
  minWordChange: number
): boolean {
  // If minWordChange is 0, any change is significant
  if (minWordChange === 0) {
    return oldContent !== newContent
  }

  const oldBag = getWordBag(oldContent)
  const newBag = getWordBag(newContent)

  // Count all word differences (added + removed)
  let totalDifferences = 0

  // Check words in old content
  for (const [word, oldCount] of oldBag) {
    const newCount = newBag.get(word) ?? 0
    totalDifferences += Math.abs(oldCount - newCount)
  }

  // Check words only in new content (not in old)
  for (const [word, newCount] of newBag) {
    if (!oldBag.has(word)) {
      totalDifferences += newCount
    }
  }

  // Use totalDifferences directly as the change count
  // - Adding 3 words = 3 differences = 3 changes ✓
  // - Removing 3 words = 3 differences = 3 changes ✓
  // - Replacing 3 words = 6 differences = 6 changes (counts as more significant, which is fine)
  return totalDifferences >= minWordChange
}

/**
 * Configuration for a lazy trigger
 */
export interface LazyTriggerConfig<T = unknown> {
  /** Unique identifier for this trigger (for logging/debugging) */
  id: string
  /** Debounce delay in milliseconds (default: 5000) */
  debounceMs?: number
  /** Minimum content length to trigger (default: 10) */
  minContentLength?: number
  /** Minimum number of words that must change to trigger (default: 0 = any change).
   *  Prevents triggering on typo fixes, punctuation changes, etc. */
  minWordChange?: number
  /** Filter function to determine which nodes to watch */
  nodeFilter: (node: Node<ReactFlowNodeData>) => boolean
  /** Extract the content to track changes for (e.g., label, description) */
  getContent: (node: Node<ReactFlowNodeData>) => string
  /** The async action to perform when triggered */
  onTrigger: (node: Node<ReactFlowNodeData>) => Promise<T>
  /** Optional callback when trigger completes successfully */
  onSuccess?: (node: Node<ReactFlowNodeData>, result: T) => void
  /** Optional callback when trigger fails */
  onError?: (node: Node<ReactFlowNodeData>, error: unknown) => void
  /** Optional: Additional check before triggering (e.g., hasn't been processed already) */
  shouldTrigger?: (node: Node<ReactFlowNodeData>) => boolean
  /** Whether to trigger immediately when a node is added (for AI-generated nodes with content) */
  triggerOnAdd?: boolean
  /** If true, only trigger when content CHANGES, not when a node is first observed (default: false) */
  onlyTriggerOnChange?: boolean
}

/**
 * State tracked for each node being watched
 */
interface NodeState {
  content: string
  lastTriggeredContent?: string
  isProcessing: boolean
}

/**
 * Return type for useLazyTrigger hook
 */
export interface UseLazyTriggerReturn {
  /** Manually trigger for a specific node (bypasses debounce) */
  triggerNow: (nodeId: string) => Promise<void>
  /** Check if a specific node is currently processing */
  isProcessing: (nodeId: string) => boolean
  /** Get all nodes currently being processed */
  getProcessingNodes: () => string[]
  /** Cancel pending trigger for a node */
  cancelPending: (nodeId: string) => void
  /** Reset state for a node (allows re-triggering) */
  resetNode: (nodeId: string) => void
}

/**
 * Hook for lazily triggering actions on nodes based on content changes.
 *
 * Features:
 * - Debounced triggering (waits for user to stop typing)
 * - Content change detection (only triggers when content actually changes)
 * - Minimum content length requirement
 * - Duplicate prevention (won't re-trigger for same content)
 * - Processing state tracking
 * - Immediate trigger option for newly added nodes
 *
 * @example
 * // Phase assignment
 * useLazyTrigger({
 *   id: 'phase-assignment',
 *   nodeFilter: (node) => node.data.category === 'idea',
 *   getContent: (node) => node.data.label || '',
 *   onTrigger: async (node) => {
 *     const result = await AIService.assignPhase(mindmapId, node.id)
 *     return result.data?.phase
 *   },
 *   onSuccess: (node, phase) => {
 *     setNodes(nodes => nodes.map(n =>
 *       n.id === node.id ? { ...n, data: { ...n.data, phase } } : n
 *     ))
 *   }
 * })
 *
 * @example
 * // Proactive generation
 * useLazyTrigger({
 *   id: 'proactive-generation',
 *   triggerOnAdd: true, // Trigger immediately for newly added nodes
 *   nodeFilter: (node) => ['venture', 'need', 'idea'].includes(node.data.category),
 *   getContent: (node) => node.data.label || '',
 *   shouldTrigger: (node) => !hasPendingSuggestions(node.id),
 *   onTrigger: async (node) => await generateForNode(node)
 * })
 */
export function useLazyTrigger<T = unknown>(
  config: LazyTriggerConfig<T>,
  enabled: boolean = true
): UseLazyTriggerReturn {
  const {
    id,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minContentLength = DEFAULT_MIN_CONTENT_LENGTH,
    minWordChange = DEFAULT_MIN_WORD_CHANGE,
    nodeFilter,
    getContent,
    onTrigger,
    onSuccess,
    onError,
    shouldTrigger,
    triggerOnAdd = false,
    onlyTriggerOnChange = false,
  } = config

  const nodes = useNodes<Node<ReactFlowNodeData>>()

  // Track pending timers for each node
  const pendingTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // Track state for each node
  const nodeStates = useRef<Map<string, NodeState>>(new Map())

  // Track nodes we've seen (for detecting newly added nodes)
  const seenNodes = useRef<Set<string>>(new Set())

  // Track nodes currently being processed
  const processingNodes = useRef<Set<string>>(new Set())

  // Track whether initial mount observation is complete
  // This prevents triggering for all existing nodes on app load
  const initialMountComplete = useRef(false)

  /**
   * Execute the trigger for a node
   */
  const executeTrigger = useCallback(
    async (node: Node<ReactFlowNodeData>) => {
      const nodeId = node.id
      const state = nodeStates.current.get(nodeId)

      // Skip if already processing
      if (processingNodes.current.has(nodeId)) {
        return
      }

      // Check additional condition if provided
      if (shouldTrigger && !shouldTrigger(node)) {
        return
      }

      processingNodes.current.add(nodeId)

      try {
        const result = await onTrigger(node)

        // Update state with last triggered content
        if (state) {
          nodeStates.current.set(nodeId, {
            ...state,
            lastTriggeredContent: state.content,
            isProcessing: false,
          })
        }

        onSuccess?.(node, result)
      } catch (error) {
        console.warn(`[useLazyTrigger:${id}] Failed to trigger for node ${nodeId}:`, error)
        onError?.(node, error)
      } finally {
        processingNodes.current.delete(nodeId)
      }
    },
    [id, onTrigger, onSuccess, onError, shouldTrigger]
  )

  /**
   * Schedule a debounced trigger for a node
   */
  const scheduleTrigger = useCallback(
    (node: Node<ReactFlowNodeData>) => {
      const nodeId = node.id

      // Clear any existing timer
      const existingTimer = pendingTimers.current.get(nodeId)
      if (existingTimer) {
        clearTimeout(existingTimer)
      }

      // Schedule new timer
      const timer = setTimeout(() => {
        pendingTimers.current.delete(nodeId)
        void executeTrigger(node)
      }, debounceMs)

      pendingTimers.current.set(nodeId, timer)
    },
    [debounceMs, executeTrigger]
  )

  // Watch nodes for changes
  useEffect(() => {
    if (!enabled) return

    // Filter to nodes we care about
    const watchedNodes = nodes.filter(nodeFilter)

    for (const node of watchedNodes) {
      const nodeId = node.id
      const content = getContent(node)
      const isNewNode = !seenNodes.current.has(nodeId)

      // Mark as seen
      seenNodes.current.add(nodeId)

      // Skip if content is too short
      if (content.trim().length < minContentLength) {
        continue
      }

      // Get or create state for this node
      const state = nodeStates.current.get(nodeId)

      if (!state) {
        // First time seeing this node with content
        nodeStates.current.set(nodeId, {
          content,
          isProcessing: false,
        })

        // If onlyTriggerOnChange is true, don't trigger for first-seen nodes
        // (only trigger when content actually changes later)
        if (onlyTriggerOnChange) {
          continue
        }

        // If triggerOnAdd and this is a new node, trigger immediately
        if (triggerOnAdd && isNewNode) {
          void executeTrigger(node)
        } else if (initialMountComplete.current) {
          // Only schedule debounced trigger AFTER initial mount is complete
          // This prevents triggering for all existing nodes on app load
          scheduleTrigger(node)
        }
        // On initial mount, we just record the state without triggering
        continue
      }

      // Check if content changed significantly (using word-level diff if minWordChange > 0)
      const isSignificantChange = hasSignificantContentChange(state.content, content, minWordChange)

      if (!isSignificantChange) {
        // DON'T update stored content for non-significant changes
        // This allows small changes to accumulate until they become significant
        // e.g., typing one word at a time will accumulate until threshold is met
        continue // Not a significant change
      }

      // Significant content change - NOW update the baseline
      // This resets accumulation after triggering regeneration
      nodeStates.current.set(nodeId, {
        ...state,
        content,
      })

      // Skip if we already triggered for this content
      if (state.lastTriggeredContent === content) {
        continue
      }

      // Schedule trigger
      scheduleTrigger(node)
    }

    // Clean up nodes that no longer exist
    const currentNodeIds = new Set(watchedNodes.map((n) => n.id))
    for (const [nodeId, timer] of pendingTimers.current) {
      if (!currentNodeIds.has(nodeId)) {
        clearTimeout(timer)
        pendingTimers.current.delete(nodeId)
        nodeStates.current.delete(nodeId)
      }
    }

    // Mark initial mount as complete after first observation
    // This allows future new nodes to trigger normally
    if (!initialMountComplete.current) {
      initialMountComplete.current = true
    }
  }, [
    nodes,
    enabled,
    nodeFilter,
    getContent,
    minContentLength,
    minWordChange,
    triggerOnAdd,
    onlyTriggerOnChange,
    scheduleTrigger,
    executeTrigger,
  ])

  // Cleanup on unmount
  useEffect(() => {
    const timers = pendingTimers.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  // Public API
  const triggerNow = useCallback(
    async (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId)
      if (!node || !nodeFilter(node)) return

      // Cancel any pending timer
      const timer = pendingTimers.current.get(nodeId)
      if (timer) {
        clearTimeout(timer)
        pendingTimers.current.delete(nodeId)
      }

      await executeTrigger(node)
    },
    [nodes, nodeFilter, executeTrigger]
  )

  const isProcessing = useCallback((nodeId: string) => {
    return processingNodes.current.has(nodeId)
  }, [])

  const getProcessingNodes = useCallback(() => {
    return Array.from(processingNodes.current)
  }, [])

  const cancelPending = useCallback((nodeId: string) => {
    const timer = pendingTimers.current.get(nodeId)
    if (timer) {
      clearTimeout(timer)
      pendingTimers.current.delete(nodeId)
    }
  }, [])

  const resetNode = useCallback(
    (nodeId: string) => {
      nodeStates.current.delete(nodeId)
      seenNodes.current.delete(nodeId)
      cancelPending(nodeId)
    },
    [cancelPending]
  )

  return {
    triggerNow,
    isProcessing,
    getProcessingNodes,
    cancelPending,
    resetNode,
  }
}

/**
 * Helper to create a node filter for specific categories
 */
export function createCategoryFilter(
  categories: string[]
): (node: Node<ReactFlowNodeData>) => boolean {
  const categorySet = new Set(categories)
  return (node) => categorySet.has(node.data.category)
}

/**
 * Helper to get label content from a node
 */
export function getLabelContent(node: Node<ReactFlowNodeData>): string {
  return node.data.label || ''
}
